// Feltmelding/PTT-panel: skriv eller SNAKK en melding som følger din posisjon
// og «lyser opp i kartet» hos alle. Naturlig språk («200 m nordvest») projiserer
// en gul markør. Innkommende meldinger kan leses opp (TTS). Bruker nettleserens
// Web Speech API (tale→tekst) + SpeechSynthesis (tekst→tale) — ingen ekstern
// tjeneste, fungerer i Chromium.

// Web Speech API er ikke i standard TS DOM-typer — hent trygt fra window.
export interface SpeechRec {
  lang: string; continuous: boolean; interimResults: boolean;
  start(): void; stop(): void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}
/**
 * ⚠ EKSPORTERT FOR `meldfra.ts` (§3: én kilde). «Meld fra»-linja er nå
 * hovedveien inn i systemet (UX-rapport 2026-08-18, F2), og den bruker SAMME
 * gjenkjenner som dette panelet. To fabrikker ville drevet fra hverandre — og en
 * talegjenkjenner som oppfører seg ulikt på to flater er verre enn én som er
 * dårlig på begge, fordi operatøren ikke kan lære seg den.
 */
export function makeRecognizer(): SpeechRec | null {
  const w = window as unknown as { SpeechRecognition?: new () => SpeechRec; webkitSpeechRecognition?: new () => SpeechRec };
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
  if (!Ctor) return null;
  const r = new Ctor();
  r.lang = 'nb-NO';
  r.continuous = false;
  r.interimResults = false;
  return r;
}

/** Les en tekst høyt (nb-NO om tilgjengelig). */
export function speak(text: string): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'nb-NO';
    synth.speak(u);
  } catch { /* TTS utilgjengelig — stille */ }
}

export interface PttOpts {
  onSend: (text: string) => void;
}

export class PttPanel {
  readonly el: HTMLElement;
  private open = false;
  private input!: HTMLTextAreaElement;
  private rec: SpeechRec | null;
  private listening = false;
  private ttsOn = true;

  constructor(private opts: PttOpts) {
    this.rec = makeRecognizer();
    this.el = this.build();
    document.body.appendChild(this.el);
  }

  get ttsEnabled() { return this.ttsOn; }
  toggle() { this.open ? this.close() : this.show(); }
  private show() { this.open = true; this.el.classList.add('open'); this.input.focus(); }
  close() { this.open = false; this.el.classList.remove('open'); this.stopListening(); }

  private build(): HTMLElement {
    const p = div('ptt-panel');
    const head = div('ptt-head');
    head.append(span('ptt-title', 'Feltmelding / PTT'));
    const x = document.createElement('button');
    x.className = 'ptt-x'; x.textContent = '×'; x.onclick = () => this.close();
    head.append(x);
    p.append(head);

    p.append(span('ptt-hint', 'Melding følger din posisjon og lyser opp i kartet. Si f.eks. «ser noe mistenkelig 200 m nordvest» → gul markør.'));

    this.input = document.createElement('textarea');
    this.input.className = 'ptt-input';
    this.input.rows = 2;
    this.input.placeholder = 'Skriv melding … (eller trykk mikrofonen)';
    this.input.onkeydown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(); } };
    p.append(this.input);

    const row = div('ptt-row');
    const mic = document.createElement('button');
    mic.className = 'ptt-mic';
    mic.textContent = this.rec ? '🎤 Snakk' : '🎤 (ikke støttet)';
    mic.disabled = !this.rec;
    mic.onclick = () => this.toggleListening(mic);
    const send = document.createElement('button');
    send.className = 'ptt-send'; send.textContent = 'Send';
    send.onclick = () => this.send();
    const tts = document.createElement('button');
    tts.className = 'ptt-tts active'; tts.textContent = '🔊 Opplesing på';
    tts.onclick = () => {
      this.ttsOn = !this.ttsOn;
      tts.classList.toggle('active', this.ttsOn);
      tts.textContent = this.ttsOn ? '🔊 Opplesing på' : '🔇 Opplesing av';
    };
    row.append(mic, send, tts);
    p.append(row);
    return p;
  }

  private send() {
    const text = this.input.value.trim();
    if (!text) return;
    this.opts.onSend(text);
    this.input.value = '';
  }

  private toggleListening(mic: HTMLButtonElement) {
    if (!this.rec) return;
    if (this.listening) { this.stopListening(); mic.classList.remove('listening'); return; }
    this.listening = true;
    mic.classList.add('listening');
    this.rec.onresult = (e) => {
      const said = Array.from(e.results).map((r) => r[0].transcript).join(' ');
      this.input.value = (this.input.value ? this.input.value + ' ' : '') + said;
    };
    this.rec.onend = () => { this.listening = false; mic.classList.remove('listening'); };
    this.rec.onerror = () => { this.listening = false; mic.classList.remove('listening'); };
    try { this.rec.start(); } catch { this.listening = false; mic.classList.remove('listening'); }
  }
  private stopListening() {
    if (this.rec && this.listening) { try { this.rec.stop(); } catch { /* noop */ } }
    this.listening = false;
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
